"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, ShieldAlert, User, HeartHandshake, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ORGANIZATION_OPTIONS = ["Police Department", "Law Enforcement Agency", "Private Individual", "Other Nonprofit", "Other"];
function AidFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground">Submission Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-2 border-b border-border pb-2">
          <User className="size-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground font-serif">Senior Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="senior_name" rules={{
          required: "Senior's Full Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Senior's Full Name <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="senior_age" rules={{
          required: "Senior's Age is required",
          min: {
            value: 0,
            message: "Age must be positive"
          },
          max: {
            value: 120,
            message: "Age must be realistic"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Senior's Age <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="number" placeholder="78" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="senior_address" rules={{
        required: "Senior's Address is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Senior's Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="123 Main St, City, State, ZIP" className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="senior_phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Senior's Phone (if known)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="aid_details" rules={{
        required: "Aid Details are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Aid Needed / Situation Details <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Brief description of the senior's needs, recent welfare check findings, etc." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6 pt-6">
        <div className="flex items-center gap-2 border-b border-border pb-2">
          <ShieldAlert className="size-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground font-serif">Submitter Information</h3>
        </div>

        <FormField control={control} name="organization_type" rules={{
        required: "Organization Type is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Submitting Organization / Affiliation <span className="text-destructive">*</span></FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ORGANIZATION_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription>Select the entity submitting this information.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="contact_name" rules={{
          required: "Your Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Your Name (Officer / Submitter) <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="Officer Jane Smith" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="contact_email" rules={{
          required: "Your Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Your Email <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane.smith@police.gov" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="follow_up_requested" render={({
        field
      }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-card/50 p-4 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Request Follow-up After Visit</FormLabel>
                <FormDescription>
                  Check if you would like the foundation to follow up with the senior after the initial visit.
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>} />
      </div>

      {submitError && <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>Submission Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="pt-4">
        <Button type="submit" size="lg" className="w-full md:w-auto" disabled={isSubmitting}>
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Submitting Securely...
            </> : <>
              <Send className="mr-2 size-5" />
              Submit Aid Request
            </>}
        </Button>
        <p className="text-xs text-muted-foreground mt-4 text-center md:text-left">
          Information submitted is kept confidential and used solely for the purpose of coordinating volunteer visitation and aid.
        </p>
      </div>
    </Form>;
}
export default function AidSubmissionForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="aid-submission-form" className="w-full py-16 md:py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-4xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-10 space-y-4">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-2">
              <HeartHandshake className="size-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight">
              Senior Aid Submission Portal
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A formal gateway for law enforcement, EMS, and the public to request compassionate aid and visitation for isolated seniors.
            </p>
          </div>

          <Alert variant="destructive" className="mb-8 border-destructive/50 bg-destructive/10 text-destructive">
            <AlertTriangle className="size-5" />
            <AlertTitle className="text-base font-semibold">Safety First: Emergency Situations</AlertTitle>
            <AlertDescription className="text-sm mt-1">
              This portal is for non-emergency visitation and basic needs coordination. If the senior is in immediate danger, experiencing a medical emergency, or requires urgent intervention, <strong>please call 911 immediately</strong>.
            </AlertDescription>
          </Alert>

          <Card className="shadow-xl shadow-primary/5 border-border/50 bg-card/95 backdrop-blur-sm">
            <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
              <CardTitle className="text-2xl font-serif">Aid Submission Form</CardTitle>
              <CardDescription className="text-base">
                Please provide as much detail as possible to help our volunteer network prepare for the visit.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <FormProvider formId="aid_submission_form_senior_aid_portal" sectionName="Aid Submission Form" defaultValues={{
              senior_name: "",
              senior_age: "",
              senior_address: "",
              senior_phone: "",
              aid_details: "",
              organization_type: "",
              contact_name: "",
              contact_email: "",
              follow_up_requested: false
            }} submitText="Submit Aid Request" successMessage="Thank you. The aid request has been securely submitted to the St. Sampson Senior Aid Foundation. Our coordination team will review the details shortly.">
                <AidFormContent />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}